import {
  extractDisplayPrice,
  extractTravellerTypePrice,
  roundMoneyAmount,
} from '@travel-planner/shared/pricing';

/**
 * XT-951 — a dynamic product's contract price for the brief (dates and party),
 * in the planner's units: what its card shows and the estimate counts, in
 * place of the product's fixed price.
 */
export type PlannerDynamicPrice = {
  price: unknown;
  pricingType: string;
  currency: string;
};

const toPositive = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const firstPositiveInList = (value: unknown): number | null => {
  if (!Array.isArray(value)) return null;
  for (const entry of value) {
    const walked = extractDisplayPrice(entry);
    if (walked != null && walked > 0) return walked;
    if (entry && typeof entry === 'object' && 'price' in entry) {
      const nested = toPositive((entry as { price: unknown }).price);
      if (nested != null) return nested;
    }
  }
  return null;
};

// List cells hit `{ price: 0, rooms: [{ price }] }` a lot — extractDisplayPrice
// returns the top-level 0 and never walks rooms. Prefer a real room/pax amount.
export const advisorClientPrice = (
  price: unknown,
  adultTravellerIds?: ReadonlySet<string>
): number | null => {
  const display = extractDisplayPrice(price, adultTravellerIds);
  if (display != null && display > 0) return display;
  if (!price || typeof price !== 'object') return display;
  const obj = price as Record<string, unknown>;
  const nested =
    firstPositiveInList(obj.rooms ?? obj.room) ??
    firstPositiveInList(obj.travellers);
  if (nested != null) return nested;
  return display != null && display > 0 ? display : null;
};

/**
 * A per-person product's Child rate (the org's child traveller types). Null
 * when it lists none or leaves it at 0 — children then pay the Adult rate.
 */
export const advisorChildPrice = (
  price: unknown,
  childTravellerIds?: ReadonlySet<string>
): number | null => {
  const child = extractTravellerTypePrice(price, childTravellerIds);
  return child != null && child > 0 ? child : null;
};

/**
 * The pricing function's answer for the brief, in the planner's units. A
 * hotel's rooms carry the whole stay; the planner prices a hotel per night,
 * so each room becomes its average night. A per-person price stays per head,
 * anything else is the booking's.
 */
export const plannerDynamicPrice = (
  row: PlannerDynamicPrice,
  nights: number
): PlannerDynamicPrice => {
  const rooms = (row.price as { rooms?: unknown } | null)?.rooms;
  if (row.pricingType !== 'Night' || !Array.isArray(rooms)) return row;
  const perNight = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value)
      ? roundMoneyAmount(value / Math.max(1, nights))
      : null;
  return {
    ...row,
    price: {
      rooms: rooms.map((room) => {
        const { roomType, roomTitle, price } = (room ?? {}) as Record<
          string,
          unknown
        >;
        return { roomType, roomTitle, price: perNight(price) };
      }),
    },
  };
};
